// lib/services/compute/yuv_format.ts
//
// Pure, dependency-free resolution of a captured frame's YUV format.
//
// The engine only uploads/computes/renders two GPU variants: I420 (3 planes,
// planar) and NV12 (2 planes, interleaved chroma). This module decides which
// one a captured frame is — strictly from the frame's own reported
// VideoFrame.format when present, then from an unambiguous copyTo layout, and
// only as an explicitly-flagged last resort from a platform preference.
//
// It never reinterprets a reported-but-unsupported format (I420A, I422, I444,
// RGBA, BGRA, NV21, …) as I420/NV12 on the basis of plane count alone.

import { YuvFormat } from "./yuvdefs";

export type FormatSource = "frame" | "layout-inference" | "platform-fallback";

export type ResolvedFormat =
  | { ok: true; format: YuvFormat; source: FormatSource; warning?: string }
  | { ok: false; reason: string };

// Minimal structural view of a VideoFrame.copyTo() PlaneLayout entry, so this
// module needs no WebGPU/DOM types and stays unit-testable.
export type PlaneLayoutLike = { offset: number; stride: number };

/**
 * Map a reported VideoFrame.format string to the supported upload set.
 *   - "I420" / "NV12" → that format
 *   - null / undefined / "" → "unknown" (frame did not report a format)
 *   - anything else → "unsupported" (explicitly reject; never reinterpret)
 */
export function normalize_reported_format(
  reported: string | null | undefined
): YuvFormat | "unsupported" | "unknown" {
  if (reported == null) return "unknown";
  const f = String(reported).trim();
  if (f === "") return "unknown";
  if (f === "I420") return "I420";
  if (f === "NV12") return "NV12";
  return "unsupported";
}

/**
 * Conservative inference used ONLY when the frame reported no format. Requires
 * an unambiguous plane count AND plausible 4:2:0 chroma geometry:
 *   - 3 planes: two equal chroma strides, each ~half the luma stride → I420.
 *     Full-width chroma (≈ luma stride) means 4:2:2 / 4:4:4, NOT 4:2:0, and is
 *     rejected here so it is never uploaded as I420.
 *   - 2 planes: one chroma stride, 0 < s ≤ luma stride              → NV12
 * Any other shape is ambiguous (format: null) and must not be guessed here.
 */
export function infer_format_from_layout(
  layout: ReadonlyArray<PlaneLayoutLike>
): { format: YuvFormat | null; reason: string } {
  const n = layout.length;
  if (n === 3) {
    const y = layout[0].stride;
    const u = layout[1].stride;
    const v = layout[2].stride;
    // 4:2:0 chroma stride ≈ y/2; 4:4:4 ≈ y. Use 0.75·y as the separating
    // threshold so padded-but-halved strides still pass while full-width
    // chroma is rejected as not-4:2:0.
    const half_max = Math.ceil(y * 0.75);
    if (y > 0 && u > 0 && v > 0 && u === v && u <= half_max) {
      return { format: "I420", reason: "3 planes with equal half-width chroma strides" };
    }
    return {
      format: null,
      reason: `3-plane layout with implausible chroma strides (y=${y}, u=${u}, v=${v}); not 4:2:0`,
    };
  }
  if (n === 2) {
    const y = layout[0].stride;
    const uv = layout[1].stride;
    if (y > 0 && uv > 0 && uv <= y) {
      return { format: "NV12", reason: "2 planes with interleaved chroma stride" };
    }
    return {
      format: null,
      reason: `2-plane layout with implausible chroma stride (y=${y}, uv=${uv})`,
    };
  }
  return { format: null, reason: `plane count ${n} is neither 2 (NV12) nor 3 (I420)` };
}

/**
 * Resolve the YUV format for one captured frame. Documented + tested order:
 *   1. Reported VideoFrame.format, normalized — I420/NV12 accepted as-is.
 *   2. Reported present but unsupported → REJECT (never reinterpret).
 *   3. Reported absent → infer from an unambiguous copyTo layout.
 *   4. Still ambiguous → platform preference, flagged as a LAST-RESORT guess.
 *
 * Note: even an accepted result is re-validated downstream by
 * YuvManager.build_descriptor (plane count/stride/bounds), so a wrong
 * last-resort guess is caught and rejected there rather than uploaded blindly.
 */
export function resolve_yuv_format(
  reported: string | null | undefined,
  layout: ReadonlyArray<PlaneLayoutLike>,
  platform_pref: YuvFormat
): ResolvedFormat {
  const norm = normalize_reported_format(reported);
  if (norm === "I420" || norm === "NV12") {
    return { ok: true, format: norm, source: "frame" };
  }
  if (norm === "unsupported") {
    return {
      ok: false,
      reason:
        `reported VideoFrame.format "${String(reported)}" is not a supported YUV format ` +
        `(only I420 and NV12 are uploaded; it was NOT reinterpreted by plane count)`,
    };
  }
  // norm === "unknown": the frame did not report a format.
  const inferred = infer_format_from_layout(layout);
  if (inferred.format) {
    return { ok: true, format: inferred.format, source: "layout-inference" };
  }
  // Last resort only: platform preference, prominently flagged as a guess.
  return {
    ok: true,
    format: platform_pref,
    source: "platform-fallback",
    warning:
      `VideoFrame.format was null/undefined and the ${layout.length}-plane layout was ambiguous ` +
      `(${inferred.reason}); falling back to the platform preference "${platform_pref}" as a ` +
      `LAST-RESORT GUESS — this frame may be rejected by descriptor validation`,
  };
}
